import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  FileText, 
  Eye, 
  X,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  GraduationCap,
  Briefcase,
  Car,
  Clock
} from 'lucide-react';
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const CVPreviewModal = ({ candidatoData, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [cvPreview, setCvPreview] = useState(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

    const handlePreviewCV = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/api/candidati/${candidatoData?.id}/cv-preview`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setCvPreview(data.cv_html);
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Errore nel caricamento anteprima');
        }
      } catch (error) {
        console.error('Errore anteprima CV:', error);
        toast.error('Errore nel caricamento anteprima');
      } finally {
        setLoading(false);
    }
  };

  const formatExperience = (experience) => {
    const mapping = {
      'nessuna': 'Nessuna esperienza',
      'meno_1_anno': 'Meno di 1 anno',
      'da_1_a_3_anni': 'Da 1 a 3 anni',
      'piu_3_anni': 'Più di 3 anni'
    };
    return mapping[experience] || experience;
  };

  if (!isOpen || !candidatoData) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" style={{overflow: 'hidden'}}>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100 flex flex-col" style={{minHeight: '400px'}}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Anteprima Curriculum Vitae
          </CardTitle>
          <div className="flex gap-2">
            <Button
              onClick={handlePreviewCV}
              disabled={loading}
              variant="outline"
              size="sm"
            >
              <Eye className="w-4 h-4 mr-2" />
              {loading ? 'Caricamento...' : 'Aggiorna Anteprima'}
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              size="sm"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {cvPreview ? (
            <div 
              className="cv-preview prose max-w-none"
              dangerouslySetInnerHTML={{ __html: cvPreview }}
            />
          ) : candidatoData ? (
            <div className="cv-content space-y-6">
              {/* Header */}
              <div className="text-center border-b pb-4">
                <h1 className="text-3xl font-bold text-gray-900">
                  {candidatoData.nome} {candidatoData.cognome}
                </h1>
                <p className="text-gray-600 mt-2">Curriculum Vitae</p>
              </div>

              {/* Informazioni Personali */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h2 className="text-xl font-semibold text-blue-600 mb-3 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Informazioni Personali
                </h2>
                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <span>{candidatoData.email}</span>
                  </div>
                  {candidatoData.telefono && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span>{candidatoData.telefono}</span>
                    </div>
                  )}
                  {candidatoData.data_nascita && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span>{new Date(candidatoData.data_nascita).toLocaleDateString('it-IT')}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span>
                      {candidatoData.citta && `${candidatoData.citta}`}
                      {candidatoData.provincia && ` (${candidatoData.provincia})`}
                      {candidatoData.cap && ` - ${candidatoData.cap}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Formazione */}
              <div>
                <h2 className="text-xl font-semibold text-blue-600 mb-3 flex items-center">
                  <GraduationCap className="w-5 h-5 mr-2" />
                  Formazione
                </h2>
                <div className="space-y-2">
                  {candidatoData.titolo_studio && (
                    <div>
                      <strong>Titolo di studio:</strong> {candidatoData.titolo_studio}
                    </div>
                  )}
                  {candidatoData.corsi_completati && (
                    <div>
                      <strong>Corsi completati:</strong> {candidatoData.corsi_completati}
                    </div>
                  )}
                  {candidatoData.certificazioni && (
                    <div>
                      <strong>Certificazioni:</strong> {candidatoData.certificazioni}
                    </div>
                  )}
                </div>
              </div>

              {/* Esperienza Professionale */}
              <div>
                <h2 className="text-xl font-semibold text-blue-600 mb-3 flex items-center">
                  <Briefcase className="w-5 h-5 mr-2" />
                  Esperienza Professionale
                </h2>
                <div className="space-y-3">
                  {candidatoData.esperienza_lavorativa && (
                    <div className="bg-gray-50 p-3 rounded">
                      <strong>Esperienza lavorativa:</strong>
                      <p className="mt-1 whitespace-pre-line">
                        {candidatoData.esperienza_lavorativa}
                      </p>
                    </div>
                  )}
                  {candidatoData.esperienza_immobiliare && (
                    <div>
                      <strong>Esperienza immobiliare:</strong>{' '}
                      <Badge variant="outline">
                        {formatExperience(candidatoData.esperienza_immobiliare)}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Disponibilità e Preferenze */}
              <div>
                <h2 className="text-xl font-semibold text-blue-600 mb-3 flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Disponibilità e Preferenze
                </h2>
                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <strong>Disponibilità orario:</strong> {candidatoData.disponibilita_orario || 'Non specificata'}
                  </div>
                  <div>
                    <strong>Disponibilità trasferte:</strong>{' '}
                    <Badge variant={candidatoData.disponibilita_trasferta ? 'default' : 'secondary'}>
                      {candidatoData.disponibilita_trasferta ? 'Sì' : 'No'}
                    </Badge>
                  </div>
                  {candidatoData.patente && (
                    <div>
                      <strong>Patente:</strong> {candidatoData.patente}
                    </div>
                  )}
                  <div>
                    <strong>Auto propria:</strong>{' '}
                    <Badge variant={candidatoData.auto_propria ? 'default' : 'secondary'}>
                      {candidatoData.auto_propria ? 'Sì' : 'No'}
                    </Badge>
                  </div>
                  {candidatoData.zona_preferenza_lavorativa && (
                    <div className="md:col-span-2">
                      <strong>Zona preferenza lavorativa:</strong> {candidatoData.zona_preferenza_lavorativa}
                    </div>
                  )}
                  {candidatoData.tipo_impiego_preferito && (
                    <div className="md:col-span-2">
                      <strong>Tipo impiego preferito:</strong> {candidatoData.tipo_impiego_preferito}
                    </div>
                  )}
                </div>
              </div>

              {/* Note Aggiuntive */}
              {candidatoData.note_pubbliche && (
                <div>
                  <h2 className="text-xl font-semibold text-blue-600 mb-3">Note Aggiuntive</h2>
                  <div className="bg-blue-50 p-3 rounded">
                    <p className="whitespace-pre-line">{candidatoData.note_pubbliche}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">Carica anteprima per visualizzare il CV</p>
              <Button
                onClick={handlePreviewCV}
                disabled={loading}
                className="mt-4"
              >
                {loading ? 'Caricamento...' : 'Carica Anteprima'}
              </Button>
            </div>
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default CVPreviewModal;